import React from "react";
import {
  MessagePrimitive,
  useMessage,
  useMessagePartImage,
  useMessagePartSource,
  useMessagePartText,
  useMessageRuntime,
  useThreadRuntime,
} from "@assistant-ui/react";
import { CircularProgress } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useTheme from "../../common/hooks/useTheme";
import ActionButton from "../../common/ui/ActionButton";
import PrimaryButton from "../../common/ui/PrimaryButton";
import SecondaryButton from "../../common/ui/SecondaryButton";
import ChatBiomarkerSuggestionCard from "./ChatBiomarkerSuggestionCard";
import { useChatSelections } from "./ChatSelectionContext";
import {
  getBiomarkerValueColor,
  getMetricStatusAppearance,
  getMetricTrendColor,
} from "./designSystem/chatSemanticColors";
import {
  getChatTooltipStyle,
} from "./designSystem/chatResponseSurface";
import { chatTypography } from "./designSystem/chatTypography";
import { resolveLinkPreview } from "./linkPreview";
import {
  BiometricsRecapPayload,
  BiomarkerDetailPayload,
  BiomarkerSuggestionItem,
  BiomarkerSuggestionsPayload,
  MetricCardPayload,
  MetricCardSuggestionsPayload,
  MealItemDetailsPayload,
  NavigationActionPayload,
  SuggestionItem,
  TrendChartPayload,
  WorkoutDetailsPayload,
} from "./types";
import MealItemDetailsCard from "./custom-cards/mealitems/MealItemDetailsCard";
import WorkoutDetailsCard from "./custom-cards/workoutdetails/WorkoutDetailsCard";
import { normalizeMarkdownText } from "./markdown";
import classes from "./Chat.module.css";

type LinkItem = {
  href: string;
  label: string;
  description?: string;
  imageSrc?: string;
  imageAlt?: string;
  showImage?: boolean;
};

type LinkListData = {
  items: LinkItem[];
};

type SuggestionsData = {
  items: SuggestionItem[];
};
type MetricCardSuggestionsData = MetricCardSuggestionsPayload;
type BiomarkerSuggestionsData = BiomarkerSuggestionsPayload;

type BiomarkerDetailData = BiomarkerDetailPayload;
type BiometricsRecapData = BiometricsRecapPayload;

type NavigationActionData = NavigationActionPayload;

type MealItemDetailsData = MealItemDetailsPayload;

type WorkoutDetailsData = WorkoutDetailsPayload;

type BiomarkerSuggestionCardProps = {
  item: BiomarkerSuggestionItem;
  selected: boolean;
  selectedLabel: string;
  onSelect: () => void;
};

const TextPart = () => {
  const part = useMessagePartText();

  return (
    <div className={`${classes.messageText} ${classes.messageMarkdown}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {normalizeMarkdownText("text" in part ? part.text : "")}
      </ReactMarkdown>
    </div>
  );
};

const ImagePart = () => {
  const part = useMessagePartImage();

  return (
    <figure className={classes.imagePart}>
      <img
        src={part.image}
        alt={part.filename}
        className={classes.inlineImage}
      />
      {part.filename && (
        <figcaption className={classes.imageCaption}>{part.filename}</figcaption>
      )}
    </figure>
  );
};

const SourcePart = () => {
  const source = useMessagePartSource();

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className={classes.sourceLink}
    >
      <span className={classes.sourceTitle}>{source.title || source.url}</span>
      <span className={classes.sourceUrl}>{source.url}</span>
    </a>
  );
};

const LinkListPart = (props: { data: LinkListData }) => {
  return (
    <div className={classes.linkList}>
      {props.data.items.map((item) => (
        <ResolvedLinkCard key={`${item.href}-${item.label}`} item={item} />
      ))}
    </div>
  );
};

const ResolvedLinkCard = (props: { item: LinkItem }) => {
  const shouldShowImage = props.item.showImage !== false;
  const [imageCandidates, setImageCandidates] = React.useState<string[]>(() =>
    shouldShowImage && props.item.imageSrc ? [props.item.imageSrc] : []
  );
  const [activeImageIndex, setActiveImageIndex] = React.useState(0);

  React.useEffect(() => {
    let isMounted = true;

    if (!shouldShowImage) {
      setImageCandidates([]);
      setActiveImageIndex(0);
      return () => {
        isMounted = false;
      };
    }

    setImageCandidates(props.item.imageSrc ? [props.item.imageSrc] : []);
    setActiveImageIndex(0);

    resolveLinkPreview(props.item.href).then((preview) => {
      if (!isMounted) {
        return;
      }

      const nextCandidates = [
        props.item.imageSrc,
        preview.imageSrc,
        preview.fallbackImageSrc,
      ].filter((value, index, array): value is string => {
        return Boolean(value) && array.indexOf(value) === index;
      });

      setImageCandidates(nextCandidates);
    });

    return () => {
      isMounted = false;
    };
  }, [props.item.href, props.item.imageSrc, shouldShowImage]);

  const activeImageSrc =
    activeImageIndex < imageCandidates.length
      ? imageCandidates[activeImageIndex]
      : undefined;

  const handleImageError = React.useCallback(() => {
    setActiveImageIndex((currentIndex) => {
      if (currentIndex + 1 < imageCandidates.length) {
        return currentIndex + 1;
      }

      return imageCandidates.length;
    });
  }, [imageCandidates.length]);

  return (
    <a
      href={props.item.href}
      target="_blank"
      rel="noreferrer"
      className={classes.linkCard}
    >
      {activeImageSrc && (
        <img
          src={activeImageSrc}
          alt={props.item.imageAlt || props.item.label}
          className={classes.linkPreviewImage}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={handleImageError}
        />
      )}
      <span className={classes.linkLabel}>{props.item.label}</span>
      {props.item.description && (
        <span className={classes.linkDescription}>
          {props.item.description}
        </span>
      )}
      <span className={classes.linkHref}>{props.item.href}</span>
    </a>
  );
};

const SuggestionsPart = (props: { data: SuggestionsData }) => {
  const threadRuntime = useThreadRuntime();

  return (
    <div className={classes.inlineSuggestions}>
      {props.data.items.map((item) => (
        <ActionButton
          key={item.id}
          label={item.label}
          onClick={() => threadRuntime.append(item.prompt)}
          sx={{
            height: "32px",
            padding: "6px 12px",
            borderRadius: "999px",
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}
        />
      ))}
    </div>
  );
};

const MetricCardContent = (props: { data: MetricCardPayload }) => {
  const theme = useTheme();
  const statusAppearance = getMetricStatusAppearance(theme, props.data.status);
  const trendColor = getMetricTrendColor(theme, props.data.trend);

  return (
    <>
      <div className={classes.metricCardHeader}>
        <div>
          <p className={classes.metricTitle}>{props.data.title}</p>
          <p className={classes.metricId}>{props.data.metricId}</p>
        </div>
        <span className={classes.metricBadge} style={statusAppearance}>
          {props.data.statusLabel}
        </span>
      </div>
      <div className={classes.metricMainRow}>
        <span className={classes.metricScore}>{props.data.score}</span>
        <span className={classes.metricTrend} style={{ color: trendColor }}>
          {props.data.trendLabel}
        </span>
      </div>
      <p className={classes.metricSummary}>{props.data.summary}</p>
      {props.data.deltaLabel && (
        <p className={classes.metricDelta} style={{ color: trendColor }}>
          {props.data.deltaLabel}
        </p>
      )}
    </>
  );
};

const MetricCardPart = (props: { data: MetricCardPayload }) => {
  return (
    <div className={classes.metricCard}>
      <MetricCardContent data={props.data} />
    </div>
  );
};

const MetricCardSuggestionsPart = (props: {
  data: MetricCardSuggestionsData;
}) => {
  const message = useMessage();
  const {
    selectedMetricPromptByMessageId,
    setSelectedMetricPrompt,
  } = useChatSelections();
  const selectedPrompt = selectedMetricPromptByMessageId[message.id];
  const threadRuntime = useThreadRuntime();

  return (
    <div className={classes.metricSuggestionList}>
      {props.data.items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`${classes.metricCard} ${classes.metricSuggestionButton} ${
            item.prompt === selectedPrompt ? classes.metricSuggestionSelected : ""
          }`}
          aria-pressed={item.prompt === selectedPrompt}
          onClick={() => {
            setSelectedMetricPrompt(message.id, item.prompt);
            threadRuntime.append(item.prompt);
          }}
        >
          <MetricCardContent data={item} />
          {item.prompt === selectedPrompt && (
            <span className={classes.metricSuggestionSelectedBadge}>
              {props.data.ui.selectedBadgeLabel}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

const BiomarkerSuggestionsPart = (props: { data: BiomarkerSuggestionsData }) => {
  const message = useMessage();
  const {
    selectedBiomarkerPromptByMessageId,
    selectBiomarkerSuggestion,
  } = useChatSelections();
  const selectedPrompt = selectedBiomarkerPromptByMessageId[message.id];

  return (
    <div className={classes.biomarkerSuggestionGroups}>
      {props.data.groups.map((group) => (
        <div key={`${props.data.metricId}-${group.testKind}`}>
          <p className={classes.biomarkerGroupTitle}>{group.testKindLabel}</p>
          <div className={classes.biomarkerSuggestionGrid}>
            {group.items.map((item) => (
              <BiomarkerSuggestionCard
                key={item.id}
                item={item}
                selectedLabel={props.data.ui.selectedBadgeLabel}
                selected={item.prompt === selectedPrompt}
                onSelect={() => {
                  selectBiomarkerSuggestion(message.id, item);
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const BiomarkerSuggestionCard = ({
  item,
  selected,
  selectedLabel,
  onSelect,
}: BiomarkerSuggestionCardProps) => {
  return (
    <ChatBiomarkerSuggestionCard
      item={item}
      selected={selected}
      selectedLabel={selectedLabel}
      onSelect={onSelect}
    />
  );
};

const BiomarkerDetailPart = (props: { data: BiomarkerDetailData }) => {
  const theme = useTheme();
  const chartData = [...props.data.values].reverse();
  const chartPoints = chartData.map((item) => ({
    ...item,
    label: item.chartLabel,
  }));
  const valueColor = getBiomarkerValueColor(theme, props.data.scoreCategory);

  return (
    <div className={classes.biomarkerDetailCard}>
      <div className={classes.biomarkerDetailHeader}>
        <div>
          <p className={classes.biomarkerDetailTitle}>{props.data.name}</p>
          <p className={classes.biomarkerDetailSubtitle}>
            {props.data.metricName}
          </p>
        </div>
        <div className={classes.biomarkerDetailMeta}>
          <span className={classes.biomarkerDetailValue} style={{ color: valueColor }}>
            {props.data.latestValueLabel}
          </span>
          <span className={classes.biomarkerDetailDate}>{props.data.latestDateLabel}</span>
        </div>
      </div>

      {chartData.length > 0 ? (
        <div className={classes.biomarkerDetailChart}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart
              data={chartPoints}
              margin={{ top: 8, right: 12, left: 0, bottom: 6 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.colors_ds.NEUTRAL_BORDER_GRAPH}
              />
              <XAxis
                dataKey="label"
                type="category"
                ticks={chartPoints.map((item) => item.label)}
                tick={{
                  ...chatTypography.chartTick,
                  fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                interval={0}
                minTickGap={0}
                padding={{ left: 12, right: 12 }}
              />
              <YAxis
                tick={{
                  ...chatTypography.chartTick,
                  fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={6}
                width={36}
              />
              <Tooltip
                formatter={(value: number) => [
                  `${value} ${props.data.unit}`,
                  props.data.ui.tooltipSeriesLabel,
                ]}
                labelFormatter={(value) =>
                  chartPoints.find((item) => item.label === String(value))
                    ?.tooltipLabel || String(value)
                }
                contentStyle={getChatTooltipStyle(theme)}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={theme.colors_ds.PRIMARY_GRAPH_MAIN}
                strokeWidth={2.5}
                dot={{
                  r: 4,
                  fill: theme.colors_ds.PRIMARY_GRAPH_MAIN,
                  strokeWidth: 0,
                }}
                activeDot={{
                  r: 5,
                  fill: theme.colors_ds.PRIMARY_GRAPH_MAIN,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className={classes.biomarkerDetailEmpty}>
          {props.data.ui.noHistoryLabel}
        </p>
      )}

      <div className={classes.biomarkerHistoryList}>
        {props.data.values.length > 0 ? (
          props.data.values.map((item) => (
            <div
              key={`${item.id}-${item.dateCreated}`}
              className={classes.biomarkerHistoryRow}
            >
              <div>
                <p className={classes.biomarkerHistoryLabel}>
                  {props.data.ui.historyValueLabel}
                </p>
                <p className={classes.biomarkerHistoryValue}>
                  {item.valueLabel}
                </p>
              </div>
              <div>
                <p className={classes.biomarkerHistoryLabel}>
                  {props.data.ui.historyDateLabel}
                </p>
                <p className={classes.biomarkerHistoryValue}>
                  {item.dateLabel}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className={classes.biomarkerDetailEmpty}>
            {props.data.ui.noHistoryEntriesLabel}
          </p>
        )}
      </div>
    </div>
  );
};

const BiometricsRecapPart = (props: { data: BiometricsRecapData }) => {
  return (
    <div className={classes.biometricsRecapCard}>
      <div className={classes.biometricsRecapHeader}>
        <div>
          <p className={classes.biometricsRecapEyebrow}>{props.data.title}</p>
          <p className={classes.biometricsRecapClientName}>
            {props.data.clientName}
          </p>
        </div>
      </div>

      <div className={classes.biometricsRecapSections}>
        {props.data.sections.map((section) => (
          <section key={section.id} className={classes.biometricsRecapSection}>
            <p className={classes.biometricsRecapSectionTitle}>
              {section.title}
            </p>
            <div className={classes.biometricsRecapGrid}>
              {section.items.map((item) => (
                <div
                  key={`${section.id}-${item.label}`}
                  className={classes.biometricsRecapItem}
                >
                  <span className={classes.biometricsRecapLabel}>
                    {item.label}
                  </span>
                  <span className={classes.biometricsRecapValue}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

const NavigationActionPart = (props: { data: NavigationActionData }) => {
  const navigate = useNavigate();

  return (
    <div className={classes.navigationActionCard}>
      {props.data.description && (
        <p className={classes.navigationActionDescription}>
          {props.data.description}
        </p>
      )}
      <PrimaryButton
        label={props.data.label}
        onClick={() => navigate(props.data.targetPath)}
        sx={{
          alignSelf: "flex-start",
        }}
      />
    </div>
  );
};

const TrendChartPart = (props: { data: TrendChartPayload }) => {
  const theme = useTheme();
  const latestPoint =
    props.data.points[props.data.points.length - 1] ||
    props.data.points[0] || {
      label: "",
      value: 0,
    };
  const trendColor = getMetricTrendColor(theme, props.data.trend);

  return (
    <div className={classes.trendChartCard}>
      <div className={classes.trendChartHeader}>
        <div>
          <p className={classes.trendChartTitle}>{props.data.title}</p>
          <p className={classes.trendChartMetric}>{props.data.metricLabel}</p>
        </div>
        <div className={classes.trendChartMeta}>
          <span className={classes.trendChartValue}>
            {props.data.latestValueLabel || `${latestPoint.value}${props.data.unit}`}
          </span>
          <span className={classes.trendChartTrend} style={{ color: trendColor }}>
            {props.data.trendLabel}
          </span>
        </div>
      </div>

      <div className={classes.trendChartCanvas}>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart
            data={props.data.points}
            margin={{ top: 8, right: 12, left: 0, bottom: 6 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.colors_ds.NEUTRAL_BORDER_GRAPH}
            />
            <XAxis
              dataKey="label"
              type="category"
              ticks={props.data.points.map((point) => point.label)}
              tick={{
                ...chatTypography.chartTick,
                fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              }}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              interval={0}
              minTickGap={0}
              padding={{ left: 12, right: 12 }}
            />
            <YAxis
              tick={{
                ...chatTypography.chartTick,
                fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
              }}
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              width={36}
            />
            <Tooltip contentStyle={getChatTooltipStyle(theme)} />
            {typeof props.data.target === "number" && (
              <ReferenceLine
                y={props.data.target}
                stroke={theme.colors_ds.NEUTRAL_BORDER_DEFAULT}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: props.data.ui.targetLabel,
                  position: "insideTopRight",
                  fill: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                  fontSize: 11,
                  fontFamily: "var(--font-Medium)",
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.colors_ds.PRIMARY_GRAPH_MAIN}
              strokeWidth={3}
              dot={{
                r: 4,
                fill: theme.colors_ds.PRIMARY_GRAPH_MAIN,
                strokeWidth: 0,
              }}
              activeDot={{
                r: 6,
                fill: theme.colors_ds.PRIMARY_GRAPH_MAIN,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className={classes.trendChartSummary}>{props.data.summary}</p>
    </div>
  );
};

const MealItemDetailsPart = (props: { data: MealItemDetailsData }) => {
  return <MealItemDetailsCard data={props.data} />;
};

const WorkoutDetailsPart = (props: { data: WorkoutDetailsData }) => {
  return <WorkoutDetailsCard data={props.data} />;
};

const MessageError = () => {
  const message = useMessage();
  const messageRuntime = useMessageRuntime();
  const status = message.status;

  if (
    !status ||
    status.type !== "incomplete" ||
    status.reason !== "error" ||
    !messageRuntime
  ) {
    return null;
  }

  const parsedError = parseMessageErrorPayload(status.error);

  return (
    <div className={classes.errorBlock}>
      <p className={classes.errorText}>{parsedError.summaryText}</p>
      {parsedError.detailsText && (
        <pre className={classes.errorDetails}>{parsedError.detailsText}</pre>
      )}
      {parsedError.retryLabel && (
        <SecondaryButton
          label={parsedError.retryLabel}
          onClick={() => messageRuntime.reload()}
          sx={{
            alignSelf: "flex-start",
            height: "32px",
            padding: "6px 12px",
            fontSize: "12px",
          }}
        />
      )}
    </div>
  );
};

const parseMessageErrorPayload = (error: unknown) => {
  if (error && typeof error === "object" && !Array.isArray(error)) {
    const maybeObject = error as {
      summaryText?: unknown;
      detailsText?: unknown;
      retryLabel?: unknown;
    };

    const summaryText =
      typeof maybeObject.summaryText === "string"
        ? maybeObject.summaryText
        : "";
    const detailsText =
      typeof maybeObject.detailsText === "string"
        ? maybeObject.detailsText
        : "";
    const retryLabel =
      typeof maybeObject.retryLabel === "string"
        ? maybeObject.retryLabel
        : undefined;

    if (summaryText || detailsText) {
      return {
        summaryText: summaryText || detailsText,
        detailsText,
        retryLabel,
      };
    }
  }

  const rawErrorText = typeof error === "string" ? error : String(error || "");
  const detailsMarker = "\nError details:\n";
  const markerIndex = rawErrorText.indexOf(detailsMarker);
  const summaryText =
    markerIndex >= 0 ? rawErrorText.slice(0, markerIndex) : rawErrorText;
  const detailsText =
    markerIndex >= 0
      ? rawErrorText.slice(markerIndex + detailsMarker.length).trim()
      : "";

  return {
    summaryText: summaryText || detailsText || String(error || ""),
    detailsText,
    retryLabel: undefined,
  };
};

const MessageLoadingState = () => {
  const theme = useTheme();
  const message = useMessage();
  const status = message.status;
  const hasContent = message.content.length > 0;

  if (status?.type !== "running" || hasContent) {
    return null;
  }

  return (
    <div className={classes.messageLoadingState}>
      <CircularProgress
        size={18}
        thickness={4.5}
        sx={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }}
      />
    </div>
  );
};

export const UserMessage = () => {
  return (
    <MessagePrimitive.Root className={`${classes.messageRow} ${classes.userRow}`}>
      <div className={`${classes.messageBubble} ${classes.userBubble}`}>
        <MessagePrimitive.Parts
          components={{
            Text: TextPart,
          }}
        />
      </div>
    </MessagePrimitive.Root>
  );
};

export const AssistantMessage = () => {
  const message = useMessage();
  const status = message.status;
  const hasContent = message.content.length > 0;
  const isRunning = status?.type === "running";
  const hasError =
    status?.type === "incomplete" && status.reason === "error";

  if (!hasContent && !isRunning && !hasError) {
    return null;
  }

  return (
    <MessagePrimitive.Root
      className={`${classes.messageRow} ${classes.assistantRow}`}
    >
      <div className={`${classes.messageBubble} ${classes.assistantBubble}`}>
        <MessageLoadingState />
        <MessagePrimitive.Parts
          components={{
            Text: TextPart,
            Image: ImagePart,
            Source: SourcePart,
            data: {
              by_name: {
                link_list: LinkListPart,
                suggestions: SuggestionsPart,
                custom_metric_card: MetricCardPart,
                metric_card_suggestions: MetricCardSuggestionsPart,
                biomarker_card_suggestions: BiomarkerSuggestionsPart,
                biomarker_detail: BiomarkerDetailPart,
                biometrics_recap: BiometricsRecapPart,
                navigation_action: NavigationActionPart,
                custom_trend_chart: TrendChartPart,
                custom_meal_item_details: MealItemDetailsPart,
                custom_workout_details: WorkoutDetailsPart,
              },
            },
          }}
        />
        <MessageError />
      </div>
    </MessagePrimitive.Root>
  );
};
